import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../configApi/apiConfig';
import { PagosTDAmortizacion } from '../PagosTDAmortizacion/PagosTDAmortizacion';
import { XMarkIcon } from '@heroicons/react/24/outline';

// ── Constantes ─────────────────────────────────────────────────────────────
const ESTADO_PAGADO    = ['2', 'PAGADO'];
const ESTADO_PENDIENTE = ['1', 'PENDIENTE'];

const AUTH_HEADERS = (token) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
});

const LOCALE_EC = 'es-EC';
const FORMAT_CURRENCY = { minimumFractionDigits: 2 };

// ── Helpers de dominio ─────────────────────────────────────────────────────
/**
 * Suma el saldo de las cuotas pendientes con retraso real (en mora).
 */
const calcularSaldoVencido = (cuotas) =>
    cuotas
        .filter((c) => ESTADO_PENDIENTE.includes(String(c.Estado)) && Number(c.Retraso || 0) > 0)
        .reduce((sum, c) => sum + Number(c.Saldo || 0), 0)
        .toFixed(2);

/**
 * Días de mora = máximo Retraso entre cuotas NO pagadas con atraso real.
 * Las cuotas ya pagadas conservan su retraso histórico pero no deben
 * inflar los días de mora actuales del crédito.
 */
const calcularDiasMora = (cuotas) =>
    cuotas.reduce((max, p) => {
        if (ESTADO_PAGADO.includes(String(p.Estado))) return max;
        const retraso = Number(p.Retraso || 0);
        return retraso > max ? retraso : max;
    }, 0);

// ── Mapas de estado ────────────────────────────────────────────────────────
const ESTADO_STYLES = {
    0: 'bg-green-100 text-green-800 border-green-300',
    1: 'bg-red-100 text-red-800 border-red-300',
    2: 'bg-blue-100 text-blue-800 border-blue-300',
    3: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
    4: 'bg-amber-100 text-amber-800 border-amber-300',
};
const ESTADO_STYLES_DEFAULT = 'bg-gray-100 text-gray-800 border-gray-300';

const ESTADO_LABELS = {
    0: 'Al Día',
    1: 'En Mora',
    2: 'Pagado',
    3: 'Abono',
    4: 'Diferimiento',
};
const ESTADO_LABEL_DEFAULT = 'Desconocido';

const ESTADO_DOT_COLOR = {
    0: 'bg-green-600',
    1: 'bg-red-600',
    2: 'bg-blue-600',
    3: 'bg-fuchsia-600',
    4: 'bg-amber-600',
};
const ESTADO_DOT_DEFAULT = 'bg-gray-600';

const getEstadoStyles       = (estado) => ESTADO_STYLES[estado]     ?? ESTADO_STYLES_DEFAULT;
const getEstadoLabel        = (estado) => ESTADO_LABELS[estado]     ?? ESTADO_LABEL_DEFAULT;
const getEstadoDotColor     = (estado) => ESTADO_DOT_COLOR[estado]  ?? ESTADO_DOT_DEFAULT;

// ── Sub-componentes ────────────────────────────────────────────────────────
const Spinner = () => (
    <div className="relative w-12 h-12">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full opacity-20 animate-pulse" />
        <div className="absolute inset-2 border-4 border-transparent border-t-blue-600 border-r-blue-500 rounded-full animate-spin" />
    </div>
);

const KpiCard = ({ label, value, subLabel, colorClass }) => (
    <div className={`flex-1 min-w-[130px] bg-white rounded-lg p-3 shadow-sm border flex flex-col justify-center border-${colorClass}-100`}>
        <div className="flex items-center justify-between mb-1">
            <p className={`text-[10px] font-bold text-${colorClass}-700 uppercase tracking-wider`}>{label}</p>
            <div className={`w-1.5 h-1.5 rounded-full bg-${colorClass}-500`} />
        </div>
        <p className={`text-lg font-bold text-${colorClass}-700 leading-none mb-1`}>{value}</p>
        <p className={`text-[9px] text-${colorClass}-600 font-medium leading-none`}>{subLabel}</p>
    </div>
);

const TableHeader = () => (
    <thead className="sticky top-0 z-20">
        <tr className="bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg">
            {['Cuota', 'Vence', 'Valor', 'Interés Total', 'Capital Total',
              'Interés', 'Capital', 'Gestión', 'Mora', 'Abono', 'Saldo Pend.', 'Retraso', 'Estado']
                .map((col, i) => (
                    <th
                        key={col}
                        className={`px-5 py-2 text-xs font-black uppercase tracking-wider text-blue-200 ${i === 0 || i === 1 ? 'text-left' : i === 12 ? 'text-center' : 'text-right'}`}
                    >
                        {col}
                    </th>
                ))}
        </tr>
    </thead>
);

const CurrencyCell = ({ value, className = 'text-gray-700' }) => (
    <td className="px-5 py-1 text-right text-sm font-semibold">
        <span className={className}>
            ${parseFloat(value).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}
        </span>
    </td>
);

const AmortizacionRow = ({ item, index, isSelected, onPress }) => {
    const estado         = item.Estado;
    const isEnMora       = estado === 1;
    const esAbono        = estado === 3;
    const esDiferimiento = estado === 4;
    const animateDot     = isEnMora || esAbono || esDiferimiento;

    const saldoClass =
        parseFloat(item.Saldo) === 0 ? 'text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg' :
        parseFloat(item.Saldo) > 0 && isEnMora ? 'text-red-600 bg-red-50 px-2.5 py-1 rounded-lg' :
        'text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg';

    const rowClass = isSelected
        ? 'bg-blue-100/80 border-l-4 border-l-blue-600 shadow-md'
        : index % 2 === 0 ? 'bg-white hover:bg-blue-50/40' : 'bg-slate-50/50 hover:bg-blue-50/40';

    return (
        <tr
            onClick={() => onPress(item.idCre_TablaDeAmortizacion, item.NumeroCuota)}
            className={`cursor-pointer transition-all duration-200 hover:bg-blue-50/80 group ${rowClass}`}
        >
            {/* Número de cuota */}
            <td className="px-5 py-1">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold text-xs shadow-sm group-hover:shadow-md transition-shadow">
                    {item.NumeroCuota}
                </span>
            </td>

            {/* Fecha de vencimiento */}
            <td className="px-5 py-1 text-sm font-medium text-gray-800">
                {new Date(item.Vence).toLocaleDateString(LOCALE_EC, {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                })}
            </td>

            {/* Montos */}
            <td className="px-5 py-1 text-right text-sm font-bold text-gray-900">
                ${parseFloat(item.ValorCuota).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}
            </td>
            <CurrencyCell value={item.InteresTotal} />
            <CurrencyCell value={item.CapitalTotal} />
            <CurrencyCell value={item.Interes} />
            <CurrencyCell value={item.Capital} />
            <CurrencyCell value={item.Gastos} />

            {/* Mora */}
            <td className="px-5 py-1 text-right text-sm">
                <span className={parseFloat(item.Mora) > 0 ? 'text-red-600 font-bold' : 'text-gray-500 font-medium'}>
                    ${parseFloat(item.Mora).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}
                </span>
            </td>

            {/* Abono */}
            <td className="px-5 py-1 text-right text-sm">
                <span className={parseFloat(item.Abono) > 0 ? 'text-green-600 font-bold' : 'text-gray-500 font-medium'}>
                    ${parseFloat(item.Abono).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}
                </span>
            </td>

            {/* Saldo pendiente */}
            <td className="px-5 py-1 text-right text-sm font-bold">
                <span className={saldoClass}>
                    ${parseFloat(item.Saldo).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}
                </span>
            </td>

            {/* Retraso */}
            <td className="px-5 py-1 text-right text-sm font-semibold text-gray-700">
                {item.Retraso} días
            </td>

            {/* Estado */}
            <td className="px-5 py-1 text-center">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border shadow-sm ${getEstadoStyles(estado)}`}>
                    <span className={`w-2 h-2 rounded-full ${getEstadoDotColor(estado)} ${animateDot ? 'animate-pulse' : ''}`} />
                    {getEstadoLabel(estado)}
                </span>
            </td>
        </tr>
    );
};

// ── Componente principal ───────────────────────────────────────────────────
export const TablaAmortizacionWeb = ({
    idCompra,
    cliente,
    cedula,
    numeroDocumento,
    onClose,
    isInline = false,
}) => {
    const [productos,     setProductos]     = useState([]);
    const [valores,       setValores]       = useState({});
    const [listaValores,  setListaValores]  = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [selectedId,    setSelectedId]    = useState(null);
    const [token,         setToken]         = useState(null);
    const [modalVisible,  setModalVisible]  = useState(false);
    const [numeroCuota,   setNumeroCuota]   = useState(0);

    // Leer token al montar
    useEffect(() => { setToken(localStorage.getItem('token')); }, []);

    // Cerrar con Escape (solo modo modal)
    useEffect(() => {
        if (isInline || !onClose) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, isInline]);

    // Carga cuotas de amortización
    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await axios.post(
                APIURL.getViewTablaAmortizacion(),
                { idCompra },
                { headers: AUTH_HEADERS(token) }
            );
            setProductos(response.data || []);
        } catch (error) {
            console.error('Error al cargar tabla de amortización:', error);
        } finally {
            setLoading(false);
        }
    }, [idCompra, token]);

    // Carga valores resumen (saldos, próxima cuota, etc.)
    const fetchDataValores = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.post(
                APIURL.getViewTablaAmortizacionValores(),
                { idCompra },
                { headers: AUTH_HEADERS(token) }
            );
            const apiValores = response.data?.[0] || {};
            setValores((prev) => ({
                ...apiValores,
                // Preservar valores calculados localmente (más exactos)
                SaldoVencido: prev.SaldoVencido ?? apiValores.SaldoVencido,
                DiasMora:     prev.DiasMora     ?? apiValores.DiasMora,
            }));
        } catch (error) {
            console.error('Error al cargar valores:', error);
        }
    }, [idCompra, token]);

    useEffect(() => {
        if (!token) return;
        fetchData();
        fetchDataValores();
    }, [token, fetchData, fetchDataValores]);

    // Recalcular métricas locales cuando cambian las cuotas
    useEffect(() => {
        if (!productos.length) return;
        setValores((prev) => ({
            ...prev,
            SaldoVencido: calcularSaldoVencido(productos),
            DiasMora:     calcularDiasMora(productos),
        }));
    }, [productos]);

    // Carga detalle de pagos de una cuota específica
    const handleRowPress = async (id, cuota) => {
        setSelectedId(id);
        setNumeroCuota(cuota);
        try {
            const response = await axios.post(
                APIURL.listTablaPagosAmortizacion(),
                { idCre_TablaDeAmortizacion: id },
                { headers: AUTH_HEADERS(token) }
            );
            setListaValores(response.data || []);
            setModalVisible(true);
        } catch (error) {
            console.error('Error al cargar pagos:', error);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    if (loading) {
        return isInline
            ? <div className="flex items-center justify-center py-12"><Spinner /></div>
            : (
                <div className="fixed inset-0 bg-gradient-to-br from-black/50 to-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-12 max-w-md w-full mx-4 shadow-2xl">
                        <div className="flex flex-col items-center justify-center gap-4">
                            <Spinner />
                            <div className="text-center">
                                <p className="text-gray-800 font-semibold text-lg">Cargando tabla de amortización</p>
                                <p className="text-gray-500 text-sm mt-2">Por favor espera un momento...</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
    }

    const content = (
        <div className={`bg-white shadow-2xl p-0 space-y-0 ${!isInline ? 'rounded-3xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col' : 'w-full flex flex-col'}`}>

            {/* Header + KPIs */}
            <div className="bg-gradient-to-r from-slate-800 via-blue-800 to-slate-800 px-6 py-4 border-b border-slate-700 flex flex-col gap-4">

                {/* Referencia y botón cerrar */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
                        <span className="px-3 py-1 bg-blue-700/30 border border-blue-400/30 rounded-lg text-blue-100 text-sm font-semibold tracking-widest">
                            Ref: #{idCompra}
                        </span>
                    </div>
                    {!isInline && onClose && (
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full text-blue-200 hover:text-white hover:bg-blue-600/50 transition-colors"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    )}
                </div>

                {/* KPIs */}
                <div className="flex flex-wrap lg:flex-nowrap gap-3 w-full">
                    <KpiCard
                        label="Vencido"
                        value={`$${parseFloat(valores.SaldoVencido || 0).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}`}
                        subLabel="En mora"
                        colorClass="red"
                    />
                    <KpiCard
                        label="Saldo Total"
                        value={`$${parseFloat(valores.SaldoTotal || 0).toLocaleString(LOCALE_EC, FORMAT_CURRENCY)}`}
                        subLabel="Por cobrar"
                        colorClass="blue"
                    />
                    <KpiCard
                        label="Próximo"
                        value={`#${valores.CuotaSiguiente || 0}`}
                        subLabel="Cuota a vencer"
                        colorClass="amber"
                    />
                    <KpiCard
                        label="Vencidas"
                        value={valores.CuotasVencidas || 0}
                        subLabel="En retraso"
                        colorClass="rose"
                    />
                    <KpiCard
                        label="Mora"
                        value={valores.DiasMora || 0}
                        subLabel="Días"
                        colorClass="orange"
                    />
                    <KpiCard
                        label="Próximo Pago"
                        value={valores.ProximoPago || 0}
                        subLabel="Días"
                        colorClass="emerald"
                    />
                </div>
            </div>

            {/* Tabla */}
            <div className="flex-1 overflow-x-auto overflow-y-auto">
                <table className="w-full border-collapse">
                    <TableHeader />
                    <tbody className="divide-y divide-gray-200">
                        {productos.map((item, index) => (
                            <AmortizacionRow
                                key={item.idCre_TablaDeAmortizacion}
                                item={item}
                                index={index}
                                isSelected={selectedId === item.idCre_TablaDeAmortizacion}
                                onPress={handleRowPress}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Footer informativo */}
            <div className="bg-slate-50 border-t border-slate-200 p-6">
                <div className="flex items-start gap-3">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-600 to-blue-400 rounded-full mt-0.5" />
                    <div>
                        <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                            Información importante
                        </p>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Haz clic en cualquier cuota para ver el historial detallado de pagos.
                            Los montos están expresados en dólares americanos (USD).
                            Todos los cálculos incluyen intereses, capital y diferenciales aplicables.
                        </p>
                    </div>
                </div>
            </div>

            {/* Modal de pagos */}
            <PagosTDAmortizacion
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                data={listaValores}
                cliente={cliente}
                cedula={cedula}
                numeroDocumento={numeroDocumento}
                numeroCuota={numeroCuota}
            />
        </div>
    );

    return isInline ? content : (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/60 via-blue-900/30 to-slate-900/60 flex items-center justify-center z-50 p-4 backdrop-blur-md">
            {content}
        </div>
    );
};

export default TablaAmortizacionWeb;
